import React, { memo, useEffect, useRef } from "react";
import { ITEMS_PER_STRAIN_PAGE } from "constants/strains";
import { useRouter } from "next/router";

import { Strain } from "custom-types/Strain";
import useFilters from "hooks/useFilters";
import useStrainsWithMenuItemAvailability from "hooks/useStrainsWithMenuItemAvailability";
import useWindowSize from "hooks/useWindowSize";
import { onClearFilter, pushPage } from "utils/strainsListsFilterUtils";

import StrainCard, {
  StrainCardAccessoryTray,
} from "components/botanic/StrainCard";
import GoogleAd from "components/GoogleAd";
import NoResults from "components/NoResults";
import PageNavigation from "components/PageNavigation";
import StrainPlaceholderList from "components/StrainList/StrainPlaceholderList";
import TrackImpression from "components/TrackImpression";

type StrainListProps = {
  adPath?: string;
  adTarget?: string;
  strains: Strain[];
  loading: boolean;
  totalCount: number;
};

const StrainListV2: React.FC<StrainListProps> = ({
  adPath,
  adTarget,
  strains,
  loading,
  totalCount,
}) => {
  const { filtersApplied } = useFilters();
  const router = useRouter();
  const { strainsWithMenuItemAvailability } =
    useStrainsWithMenuItemAvailability(strains);

  const {
    query: { page },
  } = router;

  const currentPage = parseInt(page as string) || 1;

  const { clearAllFilters } = useFilters();

  const firstThreeStrains = strains.slice(0, 3);
  const remainingStrains = strains.slice(3, strains.length);

  const { isMobile } = useWindowSize();

  // Reference the initial render
  const initialRender = useRef(true);
  useEffect(() => {
    // Only advance if this isn't the first render and the data is not loading
    if (!initialRender.current && !loading) {
      // set a timeout so if user rapidly clicks the filters
      // we don't refresh the ad a crazy amount of times
      const timer = setTimeout(() => {
        window?.googletag?.cmd?.push?.(() => {
          // refresh all ads on page, which is just the one after the first
          // three strain cards
          window.googletag.pubads?.().refresh();
        });
      }, 1000);

      return () => clearTimeout(timer);
    } else if (!loading) {
      // Loading will trigger this again after initial render so
      // once initial render is over and its not loading,
      // set the initialRender to false.
      initialRender.current = false;
    }
  }, [loading]);

  return (
    <>
      <style jsx>{`
        .isolate {
          isolation: isolate;
        }
      `}</style>
      <div className="row isolate">
        {loading ? (
          <StrainPlaceholderList count={3} />
        ) : !loading && strains?.length ? (
          firstThreeStrains?.map((strain) => {
            const hasMenuItems = strainsWithMenuItemAvailability.find(
              (str) => str.name === strain.name
            )?.hasMenuItems;

            return (
              <div className="col md:col-1/3 mb-md" key={strain.id}>
                <StrainCard
                  accessoryTray={
                    <TrackImpression
                      category="Strain Card"
                      label={hasMenuItems ? "Shopping" : "Info"}
                    >
                      <StrainCardAccessoryTray
                        strain={strain}
                        hasMenuItems={hasMenuItems}
                        trackingCategory="Strain Card"
                        trackingAction="Click"
                        trackingLabel={
                          hasMenuItems ? "Shopping - CTA" : "Info - CTA"
                        }
                      />
                    </TrackImpression>
                  }
                  disableLazyLoad={true}
                  orientation={isMobile ? "horizontal" : "vertical"}
                  preserveHeight={!isMobile}
                  strain={strain}
                  trackingLabel={`${hasMenuItems ? "Shopping" : "Info"} - Card`}
                  imageSizes={[75, null, 151, 215, null, 151]}
                />
              </div>
            );
          })
        ) : (
          <NoResults sectionName="strain-list">
            {filtersApplied ? (
              <TrackImpression
                category="Strains Filter"
                label="No Results - With active filters"
              >
                <p className="font-bold text-md">No matching strains.</p>
                <p>
                  We love your imagination, but no strains match that
                  description. Try simplifying your options or start fresh by{" "}
                  <button
                    className="font-bold text-green cursor-pointer no-underline"
                    onClick={() => onClearFilter(clearAllFilters)}
                  >
                    clearing the filters.
                  </button>
                </p>
              </TrackImpression>
            ) : (
              <TrackImpression
                category="Strains Filter"
                label="No Results - Without active filters"
              >
                <p className="font-bold text-md">No matching strains.</p>
                <p>
                  We're having trouble retrieving strains right now. Please
                  reload or try again later.
                </p>
              </TrackImpression>
            )}
          </NoResults>
        )}
      </div>
      {adPath && adTarget && (
        <div className="mb-xl mt-md gam-rect-square-fixed-height">
          <GoogleAd
            adPath={adPath}
            targeting={{ playlist: adTarget }}
            sizes={{ desktop: [728, 90], mobile: [300, 250] }}
          />
        </div>
      )}
      <div className="row">
        {loading ? (
          <StrainPlaceholderList count={3} />
        ) : !loading && remainingStrains?.length ? (
          remainingStrains.map((strain) => {
            const hasMenuItems = strainsWithMenuItemAvailability.find(
              (str) => str.name === strain.name
            )?.hasMenuItems;
            return (
              <div className="col md:col-1/3 mb-md" key={strain.id}>
                <StrainCard
                  accessoryTray={
                    <TrackImpression
                      category="Strain Card"
                      label={hasMenuItems ? "Shopping" : "Info"}
                    >
                      <StrainCardAccessoryTray
                        strain={strain}
                        hasMenuItems={hasMenuItems}
                        trackingCategory="Strain Card"
                        trackingAction="Click"
                        trackingLabel={
                          hasMenuItems ? "Shopping - CTA" : "Info - CTA"
                        }
                      />
                    </TrackImpression>
                  }
                  orientation={isMobile ? "horizontal" : "vertical"}
                  preserveHeight={!isMobile}
                  splitTrackingLabel="WSE_Strains_StrainListCardClick"
                  strain={strain}
                  imageSizes={[75, null, 151, 215, null, 151]}
                />
              </div>
            );
          })
        ) : (
          <></>
        )}
      </div>
      <PageNavigation
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / ITEMS_PER_STRAIN_PAGE)}
        onPrev={() => pushPage(router, currentPage - 1)}
        onNext={() => pushPage(router, currentPage + 1)}
        className="pt-3 md:pt-5"
      />
    </>
  );
};

// Memo cuts down renders from 4 to 1
export default memo(StrainListV2);
